import { CouponApplyErrorCode } from "../../core/enums";
import { Util } from "../../../util/util";
export class MinimumOrderAmountQualifier {
    CalculationMethod = {
        IncludeAllWithDiscountedPrice: 0,
        IncludeAllWithOriginalPrice: 1,
        ExcludeItemsWithDiscountsOrLoyalty: 2,
        ExcludeDiscountedExceptLoyaltyWithOriginalPrice: 3,
        ExcludeDiscountedExceptLoyaltyWithDiscountedPrice: 4
    };
    _appliesOnMultipleItemsTypes = [
        30 /* CouponType.PERCENTAGE_OFF_ENTIRE_ORDER */,
        33 /* CouponType.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY */,
    ];
    phase;
    constructor(phase) {
        this.phase = phase;
    }
    qualify(coupon, cart) {
        if (coupon.minimumOrderAmount === undefined || coupon.minimumOrderAmount === null || coupon.minimumOrderAmount <= 0) {
            return { isValid: true, errorCode: null };
        }
        this._calculateDiscountedQTYs(cart);
        const orderAmount = this._calculateMinimumOrderAmount(coupon, cart);
        if (orderAmount < coupon.minimumOrderAmount) {
            return { isValid: false, errorCode: CouponApplyErrorCode.MINIMUM_ORDER_AMOUNT_NOT_MET };
        }
        return { isValid: true, errorCode: null };
    }
    _calculateDiscountedQTYs(cart) {
        for (const item of cart.items) {
            let loyaltyDiscountsQty = 0;
            let nonLoyaltyDiscountsQty = 0;
            let loyaltyDiscountsAmount = 0;
            const iCoupons = cart.itemsCoupons.filter(x => x.itemKey == item.itemKey && x.halfIndex == item.halfIndex);
            for (const iCpn of iCoupons) {
                const appliedCoupon = cart.coupons.find(x => x.couponKey == iCpn.couponKey);
                if (appliedCoupon?.isReward) {
                    loyaltyDiscountsQty += 1;
                    loyaltyDiscountsAmount += Util.Float2(item.price - (appliedCoupon.amount / item.halfCount));
                }
                else {
                    nonLoyaltyDiscountsQty += 1;
                }
            }
            item.loyaltyDiscountsQTY = loyaltyDiscountsQty;
            item.nonLoyaltyDiscountsQTY = nonLoyaltyDiscountsQty;
            item.loyaltyDiscountsAmount = loyaltyDiscountsAmount;
        }
    }
    _calculateMinimumOrderAmount(coupon, cart) {
        const calcMethodId = coupon.calculationMethodID;
        // US 84040: for the two coupon types that discount across the whole order, "Allow Other
        // Coupons on Item" unchecked must drop units already discounted by another coupon from the
        // Include-All totals, in both the pre-apply and post-apply checks.
        if (!coupon.allowOtherCoupons && this._appliesOnMultipleItemsTypes.includes(coupon.couponTypeId)) {
            switch (calcMethodId) {
                case this.CalculationMethod.IncludeAllWithOriginalPrice:
                    return this._sumExcludingOtherCoupons(coupon, cart, false);
                case null:
                case undefined:
                case this.CalculationMethod.IncludeAllWithDiscountedPrice:
                    return this._sumExcludingOtherCoupons(coupon, cart, true);
            }
        }
        switch (calcMethodId) {
            case null:
            case undefined:
            case this.CalculationMethod.IncludeAllWithDiscountedPrice:
                return cart.subtotal - cart.discount;
            case this.CalculationMethod.IncludeAllWithOriginalPrice:
                return cart.subtotal;
            case this.CalculationMethod.ExcludeItemsWithDiscountsOrLoyalty:
                return this._sumNonDiscountedItems(cart);
            case this.CalculationMethod.ExcludeDiscountedExceptLoyaltyWithOriginalPrice:
                return this._sumExcludingDiscountedExceptLoyalty(cart, item => Util.Float2(item.price));
            case this.CalculationMethod.ExcludeDiscountedExceptLoyaltyWithDiscountedPrice:
                return this._sumExcludingDiscountedExceptLoyalty(cart, item => item.loyaltyDiscountsAmount);
        }
        return 0;
    }
    _sumNonDiscountedItems(cart) {
        let orderAmount = 0;
        for (const item of cart.items) {
            orderAmount += this._calculateNonDiscountedItems(item);
        }
        return orderAmount;
    }
    _sumExcludingDiscountedExceptLoyalty(cart, getLoyaltyAmount) {
        let orderAmount = 0;
        for (const item of cart.items) {
            //handle any non discounted items
            orderAmount += this._calculateNonDiscountedItems(item);
            //add loyalty-only items back at the price basis this calculation method asks for
            if (item.loyaltyDiscountsQTY > 0 && item.nonLoyaltyDiscountsQTY === 0) {
                orderAmount += getLoyaltyAmount(item);
            }
        }
        return orderAmount;
    }
    // Total the order for an "Include All" coupon that disallows other coupons on items —
    // excluding units discounted by OTHER non-loyalty coupons, while still counting this coupon's own
    // units and loyalty-redeemed units.When useDiscountedPrice is true (IncludeAllWithDiscountedPrice / unset)
    // the kept units are counted at their discounted price, so the coupon can disqualify itself post-apply;
    // otherwise (IncludeAllWithOriginalPrice) they are counted at original price.
    _sumExcludingOtherCoupons(coupon, cart, useDiscountedPrice) {
        const ownCouponKeys = new Set(cart.coupons.filter(c => c.couponId === coupon.couponId).map(c => c.couponKey));
        const otherCouponKeys = new Set(cart.coupons.filter(c => !c.isReward && !ownCouponKeys.has(c.couponKey)).map(c => c.couponKey));
        let orderAmount = 0;
        for (const item of cart.items) {
            const itemCoupons = cart.itemsCoupons.filter(ic => ic.itemKey === item.itemKey && ic.halfIndex === item.halfIndex);
            const keptQTY = item.quantity - itemCoupons.filter(ic => otherCouponKeys.has(ic.couponKey)).length;
            if (keptQTY <= 0) {
                continue;
            }
            let itemAmount = Util.Float2(item.price * keptQTY);
            if (useDiscountedPrice) {
                const ownDiscount = this._getOwnDiscount(itemCoupons, ownCouponKeys);
                const loyaltyDiscount = this._getLoyaltyDiscount(cart, item, itemCoupons);
                itemAmount = Util.Float2(itemAmount - ownDiscount - loyaltyDiscount);
            }
            orderAmount = Util.Float2(orderAmount + itemAmount);
        }
        return orderAmount;
    }
    _getOwnDiscount(itemCoupons, ownCouponKeys) {
        return itemCoupons
            .filter(ic => ownCouponKeys.has(ic.couponKey))
            .reduce((sum, ic) => Util.Float2(sum + ic.beforeTaxDiscount + ic.afterTaxDiscount), 0);
    }
    // item.loyaltyDiscountsAmount holds the discounted PRICE, not the discount, so the discount is
    // re-derived here the same way _calculateDiscountedQTYs derives that price.
    _getLoyaltyDiscount(cart, item, itemCoupons) {
        return itemCoupons.reduce((sum, ic) => {
            const appliedCoupon = cart.coupons.find(c => c.couponKey === ic.couponKey);
            return appliedCoupon?.isReward ? Util.Float2(sum + (appliedCoupon.amount / item.halfCount)) : sum;
        }, 0);
    }
    _calculateNonDiscountedItems(item) {
        let orderAmount = 0;
        const nonDiscountedQTY = item.quantity - item.nonLoyaltyDiscountsQTY - item.loyaltyDiscountsQTY;
        if (nonDiscountedQTY > 0) {
            orderAmount += Util.Float2(item.price * nonDiscountedQTY);
        }
        return orderAmount;
    }
}
//# sourceMappingURL=minimum-order-amount.js.map